import styled, { css } from 'styled-components';
import { FnButtonTypes, SIZE_VALUES, VARIANT_COLORS, WIDTH_VALUES } from './constants';
import { TStyledProps } from './types';
import { theme } from '../../../theme';

const { colors, shadows } = theme;

export const ButtonBase = styled.button`
  ${(props: TStyledProps) =>
    props &&
    css`
      display: flex;
      align-items: center;
      justify-content: center;
      outline: none;
      border: none;
      font-size: ${SIZE_VALUES[props.size].fontSize};
      padding: ${SIZE_VALUES[props.size].padding};
      min-height: ${SIZE_VALUES[props.size].minHeight};
      font-weight: 500;
      height: 100%;
      border-radius: 5px;
      user-select: none;
      color: ${colors.primary};
      background-color: transparent;
      width: ${WIDTH_VALUES[props.width]};
      transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out,
        box-shadow 0.15s ease-in-out;
      position: relative;

      :hover {
        cursor: pointer;
      }

      :focus {
        outline: none;
      }

      :disabled {
        cursor: default;
        color: ${colors.textDisabled};
        box-shadow: 0 3px 5px rgba(25, 21, 69, 0.2);

        path {
          fill: ${colors.textDisabled};
        }
      }

      svg {
        max-height: 16px;
      }

      .fn-loader-container {
        background-color: transparent;
        right: 0.8rem;
        width: unset;
        height: unset;
        top: unset;
        left: unset;

        .fn-loader {
          position: unset;
          transform: none;

          .spinner-border {
            width: 1.231rem;
            height: 1.231rem;
            border-color: ${colors.white};
            border-right-color: transparent;
          }
        }
      }
    `}
`;

const ButtonDefault = styled(ButtonBase)`
  ${(props: TStyledProps) =>
    props &&
    css`
      background-color: ${VARIANT_COLORS[props.variant].mainColor};
      border: 1px solid ${VARIANT_COLORS[props.variant].mainColor};
      color: ${colors.white};
      box-shadow: ${shadows.button};
      font-weight: bold;

      :disabled {
        color: ${colors.textDisabled};
        background-color: ${colors.disabledButtonBG};
        border-color: ${colors.disabledButtonBG};
      }

      :hover:not([disabled]) {
        background-color: ${VARIANT_COLORS[props.variant].hoverColor};
      }

      path {
        fill: ${colors.white};
      }
    `}
`;

const ButtonOutline = styled(ButtonBase)`
  ${(props: TStyledProps) =>
    props &&
    css`
      border: 1px solid ${VARIANT_COLORS[props.variant].mainColor};
      color: ${VARIANT_COLORS[props.variant].mainColor};
      background-color: ${colors.white};

      :disabled {
        border-color: ${colors.disabledButtonBG};
        background-color: ${colors.white};
      }

      :hover:not([disabled]) {
        color: ${colors.white};
        background-color: ${VARIANT_COLORS[props.variant].mainColor};

        path {
          fill: ${colors.white};
        }
      }

      path {
        fill: ${VARIANT_COLORS[props.variant].mainColor};
      }
    `}
`;

const ButtonText = styled(ButtonBase)`
  ${(props: TStyledProps) =>
    props &&
    css`
      color: ${VARIANT_COLORS[props.variant].mainColor};
      background-color: transparent;
      border: 1px solid transparent;

      :disabled {
        background-color: transparent;
        box-shadow: none;
      }

      :hover:not([disabled]) {
        color: ${colors.white};
        background-color: ${VARIANT_COLORS[props.variant].mainColor};

        path {
          fill: ${colors.white};
        }
      }

      path {
        fill: ${VARIANT_COLORS[props.variant].mainColor};
      }
    `}
`;

export const SpanStartIcon = styled.span`
  display: flex;
  margin-right: 5px;
`;

export const SpanEndIcon = styled.span`
  display: flex;
  margin-left: 5px;
`;

export const ButtonIcon = styled(ButtonText)`
  padding: 0 1rem;

  ${SpanStartIcon} {
    margin-right: 0;
  }
`;

export const BUTTONS: Record<FnButtonTypes, typeof ButtonBase> = Object.freeze({
  [FnButtonTypes.Text]: ButtonText,
  [FnButtonTypes.Default]: ButtonDefault,
  [FnButtonTypes.Outline]: ButtonOutline,
  [FnButtonTypes.Icon]: ButtonIcon
});
